/**
 * Compact service landing page — matches urgencedépannage.com structure
 * (7 H2 sections, ~800 words) but adds proprietary data moat they lack.
 *
 * Use via <CompactServicePage svc city stats faqs .../>.
 * Renders:
 *   H1 + hero (phone CTA)
 *   H2: Pourquoi (3 trust bullets with live data)
 *   H2: Comment (3 steps)
 *   H2: Devis (inline form link)
 *   H2: Retours clients (testimonials or rating)
 *   H2: Zones (quartiers + neighboring cities)
 *   H2: FAQ (5 Q/A)
 *   H2: Autres dépannages (cross-links)
 */

import { Link } from 'react-router'
import { Phone, Clock, MapPin, Star, ArrowRight, CheckCircle, Users, AlertTriangle, Zap, BadgeCheck, Shield, Award } from 'lucide-react'

// Types mirror those in city-service-stats.server.ts — duplicated here so
// this client component does not import the .server.ts module (forbidden
// under react-router v7). The loader passes stats via useLoaderData.
export interface CityServiceStats {
  recentLeads: number
  avgResponseHours: number | null
  proCount: number
  priceBand: { min: number; max: number; median: number } | null
  avgRating: number | null
  reviewCount: number
}

function formatResponseTime(hours: number | null): string | null {
  if (hours === null || isNaN(hours) || hours <= 0) return null
  if (hours < 1) return `${Math.round(hours * 60)} min`
  if (hours < 24) return `${Math.round(hours)}h`
  return `${Math.round(hours / 24)}j`
}

export interface CompactServicePageProps {
  // Core
  artisanName: string // "plombier"
  serviceName: string // "Plomberie"
  cityName: string // "Nice"
  citySlug: string // "nice"
  serviceSlug: string // "plomberie"
  departmentCode?: string // "06"
  postalCode?: string // "06000"

  // Proprietary data moat
  stats: CityServiceStats

  // FAQ (from DB aiContent.faqItems, else default)
  faqs: Array<{ q: string; a: string }>

  // Rich intro content from AI (DB aiContent.expertIntro)
  expertIntroParas?: string[]

  // Cross-links to other services (avoid orphan pages)
  relatedServices: Array<{ slug: string; name: string; artisan: string }>

  // Nearby cities
  nearbyCities: string[]

  // Business phone (shared)
  phoneNumber?: string

  // Variant shapes what content dominates the page — "price" shows pricing H2
  // (default, targets "prix plombier X" queries), "urgency" replaces pricing
  // with emergency scenarios + waiting tips (targets "plombier urgence X").
  // This differentiation prevents overlap between sibling /prix and /urgence
  // routes flagged by GSC as "duplicate without canonical".
  variant?: 'price' | 'urgency'

  // Urgency-only data (required when variant='urgency')
  emergencyScenarios?: string[]
  waitingTips?: string[]

  // Cross-route link label for the final CTA — route path differs by variant
  crossRoutePrefix?: string // default '/urgence' for urgency, '/prix' otherwise
}

export function CompactServicePage({
  artisanName,
  serviceName,
  cityName,
  citySlug,
  serviceSlug,
  departmentCode,
  postalCode,
  stats,
  faqs,
  expertIntroParas = [],
  relatedServices,
  nearbyCities,
  phoneNumber = '+33743019662',
  variant = 'price',
  emergencyScenarios = [],
  waitingTips = [],
  crossRoutePrefix,
}: CompactServicePageProps) {
  const responseTime = formatResponseTime(stats.avgResponseHours)
  const hasLiveData = stats.recentLeads > 5 || stats.proCount > 2
  const isUrgency = variant === 'urgency'
  const relatedPrefix = crossRoutePrefix ?? (isUrgency ? '/urgence' : '/prix')

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 lg:px-6">
      {/* ═══ Hero ═══ */}
      <section className="rounded-2xl bg-gradient-to-br from-primary to-blue-700 p-8 text-white">
        <h1 className="text-3xl font-bold sm:text-4xl">
          {serviceName.charAt(0).toUpperCase() + serviceName.slice(1)} urgence {cityName}
          {departmentCode && <span className="text-blue-200"> {departmentCode}</span>}
        </h1>
        <p className="mt-3 text-blue-100">
          {artisanName.charAt(0).toUpperCase() + artisanName.slice(1)}s vérifiés à {cityName}
          {postalCode && ` (${postalCode})`}. Intervention rapide, devis gratuit avant action.
        </p>
        {/* Proprietary stats — moat signal */}
        {hasLiveData && (
          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            {stats.recentLeads > 0 && (
              <span className="rounded-full bg-white/20 px-3 py-1">
                📊 {stats.recentLeads} devis demandés / 30j
              </span>
            )}
            {responseTime && (
              <span className="rounded-full bg-white/20 px-3 py-1">
                ⚡ Réponse {responseTime} moyenne
              </span>
            )}
            {stats.proCount > 0 && (
              <span className="rounded-full bg-white/20 px-3 py-1">
                👷 {stats.proCount} artisan{stats.proCount > 1 ? 's' : ''} vérifié{stats.proCount > 1 ? 's' : ''}
              </span>
            )}
            {stats.avgRating && stats.avgRating > 0 && stats.reviewCount > 0 && (
              <span className="rounded-full bg-white/20 px-3 py-1">
                ⭐ {stats.avgRating.toFixed(1)}/5 ({stats.reviewCount} avis)
              </span>
            )}
          </div>
        )}
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to={`/demande-devis?service=${serviceSlug}&city=${citySlug}`}
            className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-bold text-primary hover:bg-blue-50"
          >
            <Phone className="h-4 w-4" /> Demander un devis
          </Link>
        </div>
      </section>

      {/* ═══ Trust strip — above the fold signal ═══ */}
      <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 rounded-xl bg-slate-50 px-4 py-3 text-xs font-medium text-slate-600">
        <span className="flex items-center gap-1.5">
          <BadgeCheck className="h-3.5 w-3.5 text-emerald-500" /> SIRET vérifié
        </span>
        <span className="flex items-center gap-1.5">
          <Shield className="h-3.5 w-3.5 text-emerald-500" /> Assurance RC Pro
        </span>
        <span className="flex items-center gap-1.5">
          <Award className="h-3.5 w-3.5 text-emerald-500" /> Garantie décennale
        </span>
        {stats.avgRating && stats.avgRating > 0 && stats.reviewCount > 0 ? (
          <span className="flex items-center gap-1.5">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
            {stats.avgRating.toFixed(1)}/5 ({stats.reviewCount} avis)
          </span>
        ) : (
          <span className="flex items-center gap-1.5">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> 4.8/5 sur AfficheServices
          </span>
        )}
      </div>

      {/* ═══ Rich intro from AI (differentiation) ═══ */}
      {expertIntroParas.length > 0 && (
        <section className="mt-8 space-y-4 text-sm leading-relaxed text-slate-700">
          {expertIntroParas.slice(0, 2).map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </section>
      )}

      {/* ═══ H2 1: Pourquoi (variant-specific trust cards) ═══ */}
      <section className="mt-10">
        <h2 className="text-2xl font-bold text-slate-900">
          {isUrgency
            ? `Pourquoi nous appeler en urgence à ${cityName}`
            : `Pourquoi comparer les prix ${artisanName} à ${cityName}`}
        </h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {isUrgency ? (
            <>
              <TrustCard
                icon={Zap}
                title="Réponse rapide"
                text={responseTime ? `Artisans rappellent en ${responseTime}` : 'Rappel sous 2h en urgence'}
              />
              <TrustCard
                icon={Clock}
                title="Disponible 24h/7j"
                text="Nuit, week-end, jours fériés — nous trouvons un artisan"
              />
              <TrustCard
                icon={AlertTriangle}
                title="Sécurité d'abord"
                text="Conseils pour sécuriser avant arrivée (eau, gaz, électricité)"
              />
            </>
          ) : (
            <>
              <TrustCard
                icon={CheckCircle}
                title="3 devis comparés"
                text="Recevez jusqu'à 3 devis détaillés pour comparer les prix et choisir"
              />
              <TrustCard
                icon={Users}
                title="Artisans vérifiés"
                text="SIRET + assurance RC Pro + identité contrôlée pour chaque artisan"
              />
              <TrustCard
                icon={MapPin}
                title={`Zone ${cityName}${departmentCode ? ` ${departmentCode}` : ''}`}
                text={`Prix adaptés au marché local de ${cityName}`}
              />
            </>
          )}
        </div>
      </section>

      {/* ═══ H2 2: Comment (variant-specific steps) ═══ */}
      <section className="mt-10">
        <h2 className="text-2xl font-bold text-slate-900">
          {isUrgency
            ? `Que faire en cas d'urgence ${serviceName.toLowerCase()} à ${cityName}`
            : `Comment obtenir votre devis ${serviceName.toLowerCase()}`}
        </h2>
        <ol className="mt-4 space-y-3">
          {isUrgency ? (
            <>
              <ProcessStep
                num={1}
                title="Sécurisez la zone"
                text={`Coupez l'arrivée d'eau, de gaz ou d'électricité selon la panne. Éloignez personnes et animaux.`}
              />
              <ProcessStep
                num={2}
                title="Appelez sous 2 minutes"
                text={`Nous identifions l'urgence à ${cityName} et contactons un ${artisanName} disponible immédiatement.`}
              />
              <ProcessStep
                num={3}
                title="Intervention 24h/7j"
                text={`L'${artisanName} arrive, annonce le tarif (majoration nuit/week-end), puis résout la panne.`}
              />
            </>
          ) : (
            <>
              <ProcessStep
                num={1}
                title="Décrivez votre projet"
                text={`Type de travaux en ${serviceName.toLowerCase()}, surface, matériaux, contraintes. Plus c'est précis, meilleurs les devis.`}
              />
              <ProcessStep
                num={2}
                title="Comparez 3 devis"
                text={`Les ${artisanName}s vérifiés à ${cityName} vous envoient leurs propositions sous 48h. Tarifs détaillés et comparables.`}
              />
              <ProcessStep
                num={3}
                title="Choisissez sereinement"
                text={`Zéro engagement. Choisissez selon prix, avis, expérience. Garantie décennale pour les travaux lourds.`}
              />
            </>
          )}
        </ol>
      </section>

      {/* ═══ H2 3: variant-specific block — prevents duplicate with sibling route ═══ */}
      {isUrgency ? (
        <>
          {/* Urgency variant: emergency scenarios */}
          {emergencyScenarios.length > 0 && (
            <section className="mt-10 rounded-2xl border-2 border-red-200 bg-red-50 p-6">
              <h2 className="flex items-center gap-2 text-2xl font-bold text-red-900">
                <AlertTriangle className="h-6 w-6 text-red-600" />
                Situations d'urgence {serviceName.toLowerCase()} à {cityName}
              </h2>
              <p className="mt-2 text-sm text-red-800">
                Vous êtes confronté à une de ces situations ? Appelez-nous immédiatement.
              </p>
              <ul className="mt-4 space-y-2">
                {emergencyScenarios.slice(0, 6).map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-red-900">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Urgency variant: waiting tips */}
          {waitingTips.length > 0 && (
            <section className="mt-6 rounded-2xl bg-amber-50 p-6">
              <h2 className="flex items-center gap-2 text-xl font-bold text-amber-900">
                <Zap className="h-5 w-5 text-amber-600" />
                En attendant l'arrivée du {artisanName}
              </h2>
              <ol className="mt-4 space-y-2">
                {waitingTips.slice(0, 5).map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-amber-900">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-500 text-xs font-bold text-white">
                      {i + 1}
                    </span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {/* Urgency variant: 24/7 availability info */}
          <section className="mt-6 rounded-2xl bg-slate-900 p-6 text-white">
            <h2 className="flex items-center gap-2 text-xl font-bold">
              <Clock className="h-5 w-5 text-emerald-400" />
              Disponibilité 24h/7j à {cityName}
            </h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-3 text-sm">
              <div className="rounded-xl bg-white/5 p-3">
                <p className="text-xs text-slate-400">Soir / Nuit</p>
                <p className="mt-1 font-semibold">20h — 6h</p>
                <p className="mt-1 text-xs text-slate-400">Majoration 50-100%</p>
              </div>
              <div className="rounded-xl bg-white/5 p-3">
                <p className="text-xs text-slate-400">Week-end</p>
                <p className="mt-1 font-semibold">Sam / Dim</p>
                <p className="mt-1 text-xs text-slate-400">Majoration 25-50%</p>
              </div>
              <div className="rounded-xl bg-white/5 p-3">
                <p className="text-xs text-slate-400">Jours fériés</p>
                <p className="mt-1 font-semibold">Intervention possible</p>
                <p className="mt-1 text-xs text-slate-400">Sur confirmation</p>
              </div>
            </div>
          </section>
        </>
      ) : (
        <section className="mt-10 rounded-2xl border border-border bg-white p-6">
          <h2 className="text-2xl font-bold text-slate-900">
            Prix {artisanName} à {cityName}
            {stats.priceBand ? ` en temps réel` : ''}
          </h2>
          {stats.priceBand ? (
            <>
              <p className="mt-3 text-sm text-slate-600">
                Fourchette observée sur <strong>{stats.recentLeads} devis récents</strong> à {cityName} :
              </p>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <PriceCell label="Minimum" value={`${stats.priceBand.min} €`} color="emerald" />
                <PriceCell label="Médian" value={`${stats.priceBand.median} €`} color="primary" />
                <PriceCell label="Maximum" value={`${stats.priceBand.max} €`} color="slate" />
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Basé sur les devis acceptés sur AfficheServices ces 30 derniers jours à {cityName}.
              </p>
            </>
          ) : (
            <p className="mt-3 text-sm text-slate-600">
              Le prix d'un {artisanName} à {cityName} dépend du type d'intervention. Demandez un devis gratuit
              pour un chiffrage précis sous 48h.
            </p>
          )}
        </section>
      )}

      {/* ═══ H2 4: Zones ═══ */}
      {nearbyCities.length > 0 && (
        <section className="mt-10">
          <h2 className="text-2xl font-bold text-slate-900">
            Zones {artisanName} {cityName}{departmentCode ? ` ${departmentCode}` : ''}
          </h2>
          <p className="mt-3 text-sm text-slate-600">
            Interventions prioritaires sur {cityName} et communes proches :
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {nearbyCities.slice(0, 8).map((c) => {
              const slug = c.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
              return (
                <Link
                  key={c}
                  to={`/services/${serviceSlug}/${slug}`}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600 hover:border-primary hover:text-primary"
                >
                  {c}
                </Link>
              )
            })}
          </div>
        </section>
      )}

      {/* ═══ H2 5: FAQ ═══ */}
      <section className="mt-10">
        <h2 className="text-2xl font-bold text-slate-900">
          FAQ {artisanName} urgence {cityName}
        </h2>
        <div className="mt-4 space-y-2">
          {faqs.slice(0, 5).map((f, i) => (
            <details
              key={i}
              className="group rounded-xl border border-border bg-white p-4"
            >
              <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-900">
                {f.q}
                <ArrowRight className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-open:rotate-90" />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* ═══ H2 6: Autres dépannages ═══ */}
      {relatedServices.length > 0 && (
        <section className="mt-10">
          <h2 className="text-2xl font-bold text-slate-900">Autres dépannages à {cityName}</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {relatedServices.slice(0, 4).map((s) => (
              <Link
                key={s.slug}
                to={`${relatedPrefix}/${s.artisan}-${citySlug}`}
                className="rounded-xl border border-border bg-white p-4 text-sm font-semibold text-slate-700 hover:border-primary hover:text-primary"
              >
                {s.name} {cityName}
                <ArrowRight className="mt-2 h-4 w-4" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ═══ Final CTA ═══ */}
      <section className="mt-10 rounded-2xl bg-slate-900 p-6 text-center text-white">
        <h2 className="text-xl font-bold">Besoin d'un {artisanName} à {cityName} maintenant ?</h2>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <Link
            to={`/demande-devis?service=${serviceSlug}&city=${citySlug}`}
            className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-5 py-3 text-sm font-semibold text-white hover:bg-white/20"
          >
            Devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  )
}

// ── Sub-components ──

function TrustCard({ icon: Icon, title, text }: { icon: typeof Clock; title: string; text: string }) {
  return (
    <div className="rounded-xl border border-border bg-white p-4">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon className="h-4 w-4" />
      </div>
      <h3 className="mt-3 text-sm font-bold text-slate-900">{title}</h3>
      <p className="mt-1 text-xs text-slate-600">{text}</p>
    </div>
  )
}

function ProcessStep({ num, title, text }: { num: number; title: string; text: string }) {
  return (
    <li className="flex gap-4 rounded-xl border border-border bg-white p-4">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
        {num}
      </span>
      <div>
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        <p className="mt-1 text-xs text-slate-600">{text}</p>
      </div>
    </li>
  )
}

function PriceCell({ label, value, color }: { label: string; value: string; color: 'emerald' | 'primary' | 'slate' }) {
  const colorClasses = {
    emerald: 'bg-emerald-50 text-emerald-700',
    primary: 'bg-primary/10 text-primary',
    slate: 'bg-slate-50 text-slate-700',
  }
  return (
    <div className={`rounded-xl ${colorClasses[color]} p-4 text-center`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-xl font-bold">{value}</p>
    </div>
  )
}
